import {
    Badge,
    BadgeText,
    Box,
    Checkbox,
    CheckboxIcon,
    CheckboxIndicator,
    HStack,
    ListingSlat,
    Radio,
    RadioGroup,
    RadioIcon,
    RadioIndicator
} from "@hosttools/frontend/components";
import type {Listing} from "@hosttools/frontend/models/listing";
import type React from "react";
import {memo, useCallback, useMemo} from "react";

type Props = {
    listing: Listing;
    checked: boolean;
    connected: boolean;
    multiSelect?: boolean;
    onChange: (listingID: string, checked: boolean) => void;
};

const ListingItem: React.FC<Props> = ({listing, checked, connected, multiSelect, onChange}) => {
    const handleChange = useCallback(
        (value: boolean) => {
            onChange(listing._id, value);
        },
        [listing._id, onChange]
    );

    const handleRadioChange = useCallback(
        (isSelected: boolean) => {
            onChange(listing._id, isSelected);
        },
        [listing._id, onChange]
    );

    const listingSlat = useMemo(() => {
        return (
            <ListingSlat
                className="flex-1"
                color="blueGray.800"
                includeChannel={false}
                listing={listing}
                primaryText={listing._id}
                size="sm"
            />
        );
    }, [listing]);

    return (
        <Box className="rounded-lg border border-solid border-blueGray-200 p-4">
            {multiSelect ? (
                <Checkbox
                    className="w-full"
                    isChecked={checked || connected}
                    isDisabled={connected}
                    size="md"
                    testID="checkbox-listing-item"
                    value={listing._id}
                    onChange={handleChange}
                >
                    <HStack className="items-center w-full" space="lg">
                        <CheckboxIndicator>
                            <CheckboxIcon />
                        </CheckboxIndicator>
                        {listingSlat}
                        {listing.status && (
                            <Badge
                                action={listing.status === "listed" ? "info" : "default"}
                                className="ml-auto"
                                variant="subtle"
                            >
                                <BadgeText isTruncated>
                                    {listing.status === "listed" ? "Listed" : "Unlisted"}
                                </BadgeText>
                            </Badge>
                        )}
                    </HStack>
                </Checkbox>
            ) : (
                <RadioGroup value={checked ? listing._id : ""} onChange={handleRadioChange}>
                    <Radio className="w-full" isDisabled={connected} value={listing._id}>
                        <HStack className="items-center w-full" space="lg">
                            <RadioIndicator>
                                <RadioIcon />
                            </RadioIndicator>
                            {listingSlat}
                            {listing.status && (
                                <Badge
                                    action={listing.status === "listed" ? "info" : "default"}
                                    className="ml-auto"
                                    variant="subtle"
                                >
                                    <BadgeText isTruncated>
                                        {listing.status === "listed" ? "Listed" : "Unlisted"}
                                    </BadgeText>
                                </Badge>
                            )}
                        </HStack>
                    </Radio>
                </RadioGroup>
            )}
        </Box>
    );
};

export default memo(ListingItem);
